import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import { PROPERTY_TYPES, LISTING_TYPES, FURNISHING, AREA_UNITS, STATUSES, AMENITIES } from "../lib/constants";
import Seo from "../components/Seo";

const EMPTY = {
  title: "", description: "", listing_type: "sale", property_type: "apartment",
  city: "Jaipur", locality: "", address: "", price: "", price_period: "",
  bedrooms: 0, bathrooms: 0, balconies: 0, area_value: "", area_unit: "sqft",
  furnishing: "unfurnished", facing: "", floor: "", age: "New",
  amenities: [], images: [], status: "available", featured: false, rera_id: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-stone-700">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls = "w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand bg-white";

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editing) {
      api.get("/properties", { params: { limit: 60 } }).then((r) => {
        const found = r.data.items.find((x) => x._id === id);
        if (found) setF({ ...EMPTY, ...found });
        setLoading(false);
      });
    }
  }, [id, editing]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isResidential = !["plot", "agricultural-land"].includes(f.property_type);

  const toggleAmenity = (a) => {
    const s = new Set(f.amenities);
    s.has(a) ? s.delete(a) : s.add(a);
    set("amenities", [...s]);
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      set("images", [...f.images, ...paths]);
      toast.success(`${paths.length} image(s) uploaded`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (i) => set("images", f.images.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...f,
      price: Number(f.price) || 0,
      area_value: Number(f.area_value) || 0,
      bedrooms: Number(f.bedrooms) || 0,
      bathrooms: Number(f.bathrooms) || 0,
      balconies: Number(f.balconies) || 0,
    };
    try {
      if (editing) await api.put(`/properties/${id}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing ? "Property updated" : "Property created");
      navigate("/admin");
    } catch (err) {
      toast.error("Save failed. Check required fields.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-5 py-20"><div className="h-96 rounded-2xl bg-stone-100 animate-pulse" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
      <Seo title={`${editing ? "Edit" : "Add"} Property | Admin`} description="Manage property" />
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand mb-5"><ArrowLeft size={16} /> Back to dashboard</Link>
      <h1 className="font-heading text-3xl text-stone-900 mb-8">{editing ? "Edit Property" : "Add New Property"}</h1>

      <form onSubmit={submit} className="space-y-8" data-testid="property-form">
        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-5">
          <h2 className="font-heading text-xl text-stone-900">Basic Details</h2>
          <Field label="Title *">
            <input required value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="form-title" placeholder="e.g. Spacious 3 BHK Apartment" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Listing Type">
              <select value={f.listing_type} onChange={(e) => set("listing_type", e.target.value)} className={inputCls} data-testid="form-listing-type">
                {LISTING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Property Type">
              <select value={f.property_type} onChange={(e) => set("property_type", e.target.value)} className={inputCls} data-testid="form-property-type">
                {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="City"><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="form-city" /></Field>
            <Field label="Locality *"><input required value={f.locality} onChange={(e) => set("locality", e.target.value)} className={inputCls} data-testid="form-locality" placeholder="e.g. Vaishali Nagar" /></Field>
          </div>
          <Field label="Address"><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="form-address" /></Field>
          <Field label="Description">
            <textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="form-description" />
          </Field>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-5">
          <h2 className="font-heading text-xl text-stone-900">Pricing & Specs</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Price (₹) *"><input required type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="form-price" placeholder="e.g. 6500000" /></Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="form-status">
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Area *"><input required type="number" value={f.area_value} onChange={(e) => set("area_value", e.target.value)} className={inputCls} data-testid="form-area" /></Field>
            <Field label="Area Unit">
              <select value={f.area_unit} onChange={(e) => set("area_unit", e.target.value)} className={inputCls} data-testid="form-area-unit">
                {AREA_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </Field>
          </div>
          {isResidential && (
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Bedrooms (BHK)"><input type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className={inputCls} data-testid="form-bedrooms" /></Field>
              <Field label="Bathrooms"><input type="number" value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className={inputCls} data-testid="form-bathrooms" /></Field>
              <Field label="Furnishing">
                <select value={f.furnishing} onChange={(e) => set("furnishing", e.target.value)} className={inputCls} data-testid="form-furnishing">
                  {FURNISHING.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Facing"><input value={f.facing} onChange={(e) => set("facing", e.target.value)} className={inputCls} data-testid="form-facing" placeholder="e.g. East" /></Field>
            <Field label="Floor"><input value={f.floor} onChange={(e) => set("floor", e.target.value)} className={inputCls} data-testid="form-floor" placeholder="e.g. 3 of 5" /></Field>
            <Field label="RERA ID"><input value={f.rera_id} onChange={(e) => set("rera_id", e.target.value)} className={inputCls} data-testid="form-rera" /></Field>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" className="w-4 h-4 accent-[#C87968]" />
            Mark as Featured (shown on homepage)
          </label>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          <h2 className="font-heading text-xl text-stone-900">Amenities</h2>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map((a) => (
              <button type="button" key={a} onClick={() => toggleAmenity(a)} data-testid={`form-amenity-${a}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${
                  f.amenities.includes(a) ? "bg-brand text-white border-brand" : "border-stone-200 text-stone-600 hover:border-brand"}`}>
                {a}
              </button>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          <h2 className="font-heading text-xl text-stone-900">Photos</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {f.images.map((img, i) => (
              <div key={`${img}-${i}`} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-stone-200" data-testid={`form-image-${i}`}>
                <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} data-testid={`remove-image-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={14} />
                </button>
              </div>
            ))}
            <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-stone-300 grid place-items-center cursor-pointer hover:border-brand transition-colors" data-testid="upload-label">
              {uploading ? <Loader2 className="animate-spin text-brand" /> : (
                <div className="text-center text-stone-400">
                  <Upload size={22} className="mx-auto mb-1" />
                  <span className="text-xs">Upload</span>
                </div>
              )}
              <input type="file" accept="image/*" multiple hidden onChange={upload} data-testid="form-image-input" />
            </label>
          </div>
        </section>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="form-submit"
            className="rounded-full bg-brand text-white px-8 py-3 text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-60">
            {saving ? "Saving…" : editing ? "Update Property" : "Create Property"}
          </button>
          <Link to="/admin" className="rounded-full border border-stone-200 px-8 py-3 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default PropertyForm;
